import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CameraFilled,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  describeError,
  isGeneratedMeterNo,
  METER_STATUS_LABELS,
  matchesWords,
  moveInRound,
  present,
  reorderMutation,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Input,
  InputNumber,
  Segmented,
  Table,
  Tag,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useClient, useQuery } from 'urql';
import { MeterPanel } from './MeterPanel';
import { useSplit } from './panel';
import { type MeterRow, MetersDocument, peopleContext } from './people-data';
import { useFitColumns } from './table-fit';
import { ListHeader, TableCard } from './ui';
import { useIsCommittee } from './water-data';

type View = 'round' | 'other' | 'all';

/**
 * «العدادات»: the round in the reader's order, the meters out of it, and the
 * round's order itself (arrows or a position, saved at once: the readings
 * and printed invoices follow it). A row opens its panel.
 */
export function MetersScreen() {
  const { ref: fitRef, fit } = useFitColumns();
  const canEdit = useIsCommittee();
  const { message } = App.useApp();
  const client = useClient();
  const split = useSplit();
  const navigate = useNavigate();
  // A row opens the record's page (doc 58); old `?panel=<id>` links too.
  // The panel stays for a new record.
  useEffect(() => {
    if (split.panel && split.panel !== 'new') {
      navigate(`/meters/${split.panel}`, { replace: true });
    }
  }, [split.panel, navigate]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('round');
  const [reordering, setReordering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [{ data, error, fetching }] = useQuery({
    query: MetersDocument,
    context: useMemo(() => peopleContext, []),
  });
  const all = useMemo(() => present(data?.meters?.nodes), [data]);
  const round = useMemo(
    () => all.filter((m) => m.meterStatus === 'CONNECTED'),
    [all],
  );
  const [order, setOrder] = useState<MeterRow[]>([]);
  useEffect(() => {
    if (!reordering) setOrder(round);
  }, [round, reordering]);

  const list =
    view === 'round'
      ? reordering
        ? order
        : round
      : view === 'other'
        ? all.filter((m) => m.meterStatus !== 'CONNECTED')
        : all;
  const rows = reordering
    ? list
    : list.filter((m) =>
        matchesWords(
          [m.label, m.meterNo, m.meterAddress, m.member?.fullName],
          search,
        ),
      );

  async function move(index: number, delta: number) {
    const { order: next, changes } = moveInRound(order, index, delta);
    if (!changes.length) return;
    const previous = order;
    setOrder(next);
    setSaving(true);
    const { query, variables } = reorderMutation(changes);
    const r = await client
      .mutation(query, variables, peopleContext)
      .toPromise();
    setSaving(false);
    if (r.error) {
      setOrder(previous);
      message.error(describeError(r.error, 'تعذّر حفظ الترتيب.'));
    }
  }

  const position = (m: MeterRow) => round.indexOf(m) + 1;

  return (
    <div className="desk-split">
      <section className="desk-page" ref={fitRef}>
        <ListHeader
          title="العدادات"
          count={data ? all.length : undefined}
          description="العدادات بترتيب جولة القارئ. يُفتح العداد في صفحته."
          actions={
            <>
              {canEdit && view === 'round' && (
                <Button
                  type={reordering ? 'primary' : 'default'}
                  loading={saving}
                  onClick={() => {
                    setReordering(!reordering);
                    setSearch('');
                  }}
                >
                  {reordering ? 'انتهيت من الترتيب' : 'ترتيب الجولة'}
                </Button>
              )}
              {canEdit && !reordering && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => split.open('new')}
                >
                  عداد جديد
                </Button>
              )}
            </>
          }
        />
        <TableCard
          toolbar={
            !reordering && (
              <>
                <Segmented<View>
                  value={view}
                  onChange={setView}
                  options={[
                    { value: 'round', label: `في الجولة (${round.length})` },
                    {
                      value: 'other',
                      label: `غير مربوطة (${all.length - round.length})`,
                    },
                    { value: 'all', label: `الكل (${all.length})` },
                  ]}
                />
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="بحث: العداد، العضو، العنوان"
                  className="desk-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </>
            )
          }
          bars={
            (reordering || error) && (
              <>
                {reordering && (
                  <Alert
                    type="info"
                    showIcon
                    message="رتّب العدادات حسب طريق القارئ: بالأسهم أو بكتابة الرتبة. يُحفظ كل تغيير فوراً، ويتبعه ترتيب القراءات والفواتير المطبوعة."
                  />
                )}
                {error && (
                  <Alert type="error" showIcon message="تعذّر تحميل العدادات" />
                )}
              </>
            )
          }
        >
          <Table<MeterRow>
            rowKey="id"
            size="small"
            tableLayout="fixed"
            dataSource={rows}
            loading={fetching && !data}
            pagination={false}
            scroll={{ y: 'calc(100vh - 240px)' }}
            rowClassName={(m) =>
              reordering
                ? ''
                : m.id === split.panel
                  ? 'is-open is-clickable'
                  : 'is-clickable'
            }
            onRow={(m) =>
              reordering ? {} : { onClick: () => navigate(`/meters/${m.id}`) }
            }
            locale={{ emptyText: search ? 'لا عداد بهذا البحث' : 'لا شيء' }}
            columns={fit<MeterRow>([
              ...(view === 'round'
                ? [
                    {
                      key: 'position',
                      title: '#',
                      width: reordering ? 190 : 56,
                      render: (_: unknown, m: MeterRow, index: number) =>
                        reordering ? (
                          <span className="desk-reorder">
                            <InputNumber
                              size="small"
                              min={1}
                              max={order.length}
                              value={index + 1}
                              aria-label="الرتبة"
                              onPressEnter={(e) => {
                                const to = Number(
                                  (e.target as HTMLInputElement).value,
                                );
                                if (to >= 1) move(index, to - 1 - index);
                              }}
                              onBlur={(e) => {
                                const to = Number(e.target.value);
                                if (to >= 1 && to !== index + 1) {
                                  move(index, to - 1 - index);
                                }
                              }}
                            />
                            <Button
                              size="small"
                              icon={<ArrowUpOutlined />}
                              aria-label="إلى الأعلى"
                              disabled={index === 0 || saving}
                              onClick={() => move(index, -1)}
                            />
                            <Button
                              size="small"
                              icon={<ArrowDownOutlined />}
                              aria-label="إلى الأسفل"
                              disabled={index === order.length - 1 || saving}
                              onClick={() => move(index, 1)}
                            />
                          </span>
                        ) : (
                          <span className="ltr-num">{position(m)}</span>
                        ),
                    },
                  ]
                : []),
              {
                key: 'member',
                title: 'العضو',
                ellipsis: true,
                render: (_, m) => m.member?.fullName,
              },
              {
                key: 'meter',
                title: 'العداد',
                ellipsis: true,
                render: (_, m) => (
                  <span>
                    {m.label}
                    {m.meterAddress !== m.label && (
                      <span className="field-hint"> · {m.meterAddress}</span>
                    )}
                  </span>
                ),
              },
              ...(reordering
                ? []
                : [
                    {
                      key: 'number',
                      drop: 2,
                      title: 'الرقم',
                      width: 150,
                      render: (_: unknown, m: MeterRow) =>
                        isGeneratedMeterNo(m.meterNo) ? (
                          <span className="field-hint">—</span>
                        ) : (
                          <span className="ltr-num">{m.meterNo}</span>
                        ),
                    },
                    {
                      key: 'photo',
                      drop: 1,
                      title: 'صورة',
                      width: 60,
                      render: (_: unknown, m: MeterRow) =>
                        m.photo ? (
                          <CameraFilled
                            className="receipt-mark"
                            aria-label="له صورة"
                          />
                        ) : null,
                    },
                  ]),
              {
                key: 'status',
                title: 'الحالة',
                width: 100,
                render: (_, m) => (
                  <Tag
                    color={m.meterStatus === 'CONNECTED' ? 'green' : 'default'}
                  >
                    {METER_STATUS_LABELS[m.meterStatus]}
                  </Tag>
                ),
              },
            ])}
          />
        </TableCard>
      </section>
      {split.panel === 'new' && !reordering && (
        <MeterPanel
          key="new"
          id={null}
          onClose={split.close}
          onOpen={(id) => navigate(`/meters/${id}`)}
          onDirtyChange={split.onDirtyChange}
        />
      )}
    </div>
  );
}
